import React, { useState } from 'react';
import {
  Box,
  Button,
  TextField,
  Paper,
  CircularProgress,
  Alert,
  Typography,
} from '@mui/material';
import RichTextEditor from '../Common/RichTextEditor';
import TagEditor from '../Common/TagEditor';
import NicknamesEditor from '../Common/NicknamesEditor';
import RelationshipEditor from '../Common/RelationshipEditor';
import AttachmentManager from '../Common/AttachmentManager';
import { wikiTheme } from '../wikiTheme';
import * as wikiService from '../../../services/wikiService';

/**
 * Create/Edit Location component with relationship management
 */
const LocationForm = ({ 
  onSuccess, 
  existingEntry = null, 
  suggestedTags = [] 
}) => {
  const [title, setTitle] = useState(existingEntry?.title || '');
  const [nicknames, setNicknames] = useState(existingEntry?.nicknames || '');
  const [description, setDescription] = useState(existingEntry?.description || '');
  const [tags, setTags] = useState(existingEntry?.tags || []);
  const [attachments, setAttachments] = useState(existingEntry?.attachments || []);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!title.trim()) {
      setError('Title is required');
      return;
    }

    try {
      setLoading(true);
      setError('');

      if (existingEntry) {
        await wikiService.updateWikiEntry(existingEntry.id, {
          title,
          nicknames,
          description,
          tags,
          attachments,
        });
        onSuccess();
      } else {
        await wikiService.createWikiEntry({
          type: 'location',
          title,
          nicknames,
          description,
          tags,
          attachments,
          createdBy: 'user',
        });
        onSuccess();
      }

      setLoading(false);
    } catch (err) {
      setError(err.message);
      setLoading(false);
    }
  };

  return (
    <Paper sx={{ p: 3, backgroundColor: wikiTheme.mediumGray, borderLeft: `4px solid ${wikiTheme.primary}` }}>
      <form onSubmit={handleSubmit}>
        {error && <Alert severity="error" sx={{ mb: 2, fontFamily: wikiTheme.font }}>{error}</Alert>}

        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
          {/* Location Name */}
          <Box>
            <TextField
              label="Location Name"
              value={title}
              onChange={(e) => setTitle(e.target.value)}
              fullWidth
              required
              placeholder="e.g., Tavern of Moonbow, Dragon's Lair"
              sx={{
                '& .MuiOutlinedInput-root': {
                  color: wikiTheme.secondary,
                  fontFamily: wikiTheme.font,
                  '& fieldset': {
                    borderColor: wikiTheme.lightGray,
                  },
                  '&:hover fieldset': {
                    borderColor: wikiTheme.primary,
                  },
                  '&.Mui-focused fieldset': {
                    borderColor: wikiTheme.primary,
                  },
                },
                '& .MuiInputBase-input::placeholder': {
                  color: wikiTheme.tertiary,
                  opacity: 0.7,
                },
                '& .MuiInputLabel-root': {
                  color: wikiTheme.tertiary,
                  fontFamily: wikiTheme.font,
                },
                '& .MuiInputLabel-root.Mui-focused': {
                  color: wikiTheme.primary,
                },
              }}
            />
          </Box>

          {/* Nicknames */}
          <Box>
            <Typography variant="subtitle2" sx={{ mb: 1, color: wikiTheme.primary, fontFamily: wikiTheme.font, fontWeight: 'bold' }}>
              Nicknames
            </Typography>
            <NicknamesEditor
              nicknames={nicknames}
              onChange={setNicknames}
            />
          </Box>

          {/* Tags */}
          <Box>
            <Typography variant="subtitle2" sx={{ mb: 1, color: wikiTheme.primary, fontFamily: wikiTheme.font, fontWeight: 'bold' }}>
              Tags
            </Typography>
            <TagEditor
              tags={tags}
              onChange={setTags}
              suggestedTags={suggestedTags}
            />
          </Box>

          {/* Description */}
          <Box>
            <Typography variant="subtitle2" sx={{ mb: 1, color: wikiTheme.primary, fontFamily: wikiTheme.font, fontWeight: 'bold' }}>
              Description
            </Typography>
            <Box sx={{ 
              '& .quill': {
                backgroundColor: wikiTheme.dark,
              },
              '& .ql-container': { 
                minHeight: '200px',
                maxHeight: '300px',
                fontSize: '14px',
                overflow: 'auto',
              },
              '& .ql-editor': {
                minHeight: '200px',
                maxHeight: '300px',
              }
            }}>
              <RichTextEditor
                value={description}
                onChange={setDescription}
                placeholder="Describe the location..."
              />
            </Box>
          </Box>

          {/* Relationships */}
          {existingEntry && (
            <Box>
              <Typography variant="subtitle2" sx={{ mb: 1, color: wikiTheme.primary, fontFamily: wikiTheme.font, fontWeight: 'bold' }}>
                Relationships
              </Typography>
              <RelationshipEditor
                entryId={existingEntry.id}
                currentRelationships={existingEntry.relationships || []}
                onRelationshipChange={() => {}}
                entryType="location"
              />
            </Box>
          )}

          {/* Attachments */}
          {existingEntry && (
            <Box>
              <AttachmentManager
                entryId={existingEntry.id}
                attachments={attachments}
                onAttachmentsChange={setAttachments}
              />
            </Box>
          )}

          {/* Submit Button */}
          <Box sx={{ display: 'flex', gap: 2, pt: 1 }}>
            <Button
              type="submit"
              variant="contained"
              disabled={loading}
              sx={{
                backgroundColor: wikiTheme.primary,
                color: wikiTheme.dark,
                fontFamily: wikiTheme.font,
                fontWeight: 'bold',
                px: 4,
                py: 1.5,
                fontSize: '1rem',
                '&:hover': {
                  backgroundColor: '#e0a23d',
                },
              }}
            >
              {loading ? <CircularProgress size={24} sx={{ color: wikiTheme.dark }} /> : (existingEntry ? 'Update Location' : 'Create Location')}
            </Button>
          </Box>
        </Box>
      </form>
    </Paper>
  );
};

export default LocationForm;
